import * as React from 'react'

import { Box, Button, matchText } from '@factorialco/deprecated-design-system'
import { LOCAL_STORAGE_KEYS } from 'config/Vars'
import { entries, flatten, values } from 'lodash'
import isEmpty from 'lodash/isEmpty'

import { observer } from 'mobx-react'

import Callout from 'modules/core/components/Callout'
import { getFilterValue } from 'modules/core/components/EmployeeMultiFilters/commonFilters'
import useEmployeeMultiFilters from 'modules/core/components/EmployeeMultiFilters/hooks/useEmployeeMultiFilters'
import createComputed from 'modules/core/hooks/createComputed'
import useAsyncAction from 'modules/core/hooks/useAsyncAction'
import { useUnsafeEffect } from 'modules/core/hooks/useEffect'
import { useDebouncedSearch } from 'modules/core/hooks/useSearch'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import asyncAction from 'modules/core/lib/asyncAction'
import type FiltersForm from 'modules/core/lib/forms/Filters'
import i18n from 'modules/core/lib/i18n'
import useLocalStorage from 'modules/core/lib/useLocalStorage'
import { employeeUser } from 'modules/core/store/collections/users'

import { type LegalEntity } from 'modules/employees/hooks/useLegalEntities'
import useChangesForEmployees, {
  ChangeActionType,
} from 'modules/employees/pages/EditInBulk/hooks/useChangesForEmployees'
import useTableSelection from 'modules/employees/pages/EditInBulk/hooks/useTableSelection'
import {
  CustomField,
  EmployeeCustomFieldsValue,
  EmployeeGroups,
} from 'modules/employees/pages/EditInBulk/types'
import changesByEmployeeToBackend from 'modules/employees/pages/EditInBulk/utils/changesByEmployeeToBackend'
import employeesCollection from 'modules/employees/store/collections/employees'
import type Employee from 'modules/employees/store/models/Employee'
import { ResponsivePage } from 'modules/layout/components/ResponsivePage'

import { DispatchLocalChangeOnEmployeesProvider } from './Context/dispatchLocalChangeOnEmployeesContext'
import { LocalChangesByEmployeeProvider } from './Context/localChangesByEmployeeContext'
import Body from './_Body'
import EditModal from './_EditModal'
import SearchBar from './_SearchBar'

interface Props {
  employees: Employee[]
  legalEntities: LegalEntity[]
  filtersForm: FiltersForm
  canSeeAdminWelcomeSpace: boolean
  employeeGroups: EmployeeGroups[]
  customFields: CustomField[]
  customFieldsValues: EmployeeCustomFieldsValue[]
}

const useSearchFilter = createComputed((search: string, employees: Employee[]) => {
  return employees.filter((employee) => {
    return isEmpty(search) || matchText(search, employeeUser(employee).fullName())
  })
})

const ListEmployees = ({
  employees,
  legalEntities,
  filtersForm,
  employeeGroups,
  customFields,
  customFieldsValues,
}: Props) => {
  const [, setLocalFilterByValue] = useLocalStorage<string>(LOCAL_STORAGE_KEYS.employeesFilter)

  const { tableSelection, clearTableSelection, onSelect, onAllSelect } = useTableSelection({
    customFields,
  })

  const { localChangesByEmployee, dispatchLocalChangeOnEmployees, isLocalChangesToBePushed } =
    useChangesForEmployees()

  useUnsafeEffect(() => {
    setLocalFilterByValue(filtersForm.get('filterBy').value)
  }, [filtersForm, setLocalFilterByValue])

  const { search, debouncedSearch, onSearch } = useDebouncedSearch()

  const {
    filteredModels: filteredEmployees,
    logicOperator,
    filters,
    onApplyFilters,
    onCancel,
    onRemoveFilter,
    onAddFilter,
  } = useEmployeeMultiFilters<Employee>(getFilterValue, filtersForm.get('filterBy').value)

  const employeesToShow = useSearchFilter(debouncedSearch, filteredEmployees).get()

  const { performAction: saveChanges } = useAsyncAction(
    async () => {
      return await asyncAction(async () => {
        await employeesCollection.bulkUpdate(changesByEmployeeToBackend(localChangesByEmployee))
        dispatchLocalChangeOnEmployees({ type: ChangeActionType.MARK_CHANGES_AS_PUSHED })
        track(trackingEvents.editEmployeesInBulk.saveBulkEdit)
        clearTableSelection()
      })
    },
    {
      successMessage: i18n.t('employees.bulk_edit.success_message'),
      successFlashTime: 10 * 1000,
    }
  )

  const resetChanges = () => {
    dispatchLocalChangeOnEmployees({ type: ChangeActionType.RESET_TO_LAST_PUSHED })
    track(trackingEvents.editEmployeesInBulk.resetBulkEdit)
    clearTableSelection()
  }

  const isChecked = flatten(values(tableSelection)).length > 0
  const disableSaveChanges = !isLocalChangesToBePushed
  const [showModal, setShowModal] = React.useState(false)

  const handleOnClickModify = () => {
    const tableSelectionKeys = entries(tableSelection)
      .filter(([_, value]) => value.length > 0)
      .map(([key, _]) => key)
    track(trackingEvents.editEmployeesInBulk.employeesBulkEdit, {
      selected_fields: tableSelectionKeys,
    })
    setShowModal(true)
  }

  const TableOptions = (
    <Box
      width="full"
      flexDirection="row"
      justifyContent="spaceBetween"
      alignItems="center"
      gap="s8"
    >
      <Box flexDirection="row" gap="s8">
        <SearchBar
          search={search}
          onSearch={onSearch}
          filtersForm={filtersForm}
          filters={filters}
          logicOperator={logicOperator}
          onApplyFilters={(defaultFilter) => onApplyFilters(employees, defaultFilter)}
          onCancel={onCancel}
          onRemoveFilter={onRemoveFilter}
          onAddFilter={onAddFilter}
        />
      </Box>
      <Box flexDirection="row" gap="s8">
        <Button
          disabled={disableSaveChanges}
          type="secondary"
          onClick={resetChanges}
          iconName="cross"
        >
          {i18n.t('employees.reset_changes')}
        </Button>
        <Button
          type="secondary"
          onClick={() => handleOnClickModify()}
          iconName="pencil"
          disabled={!isChecked}
        >
          {i18n.t('employees.modify_data')}
        </Button>
      </Box>
      {showModal && (
        <EditModal
          tableSelection={tableSelection}
          onClose={() => setShowModal(false)}
          legalEntities={legalEntities}
          employeeGroups={employeeGroups}
          customFields={customFields}
        />
      )}
    </Box>
  )

  return (
    <ResponsivePage.Body
      variant="fullscreen"
      showHeader={false}
      transactionalActions={{
        primary: {
          label: i18n.t('employees.save_changes'),
          onClick: () => saveChanges(),
          disabled: disableSaveChanges,
        },
      }}
    >
      <Box gap="s24" padding="s24" height="full">
        {!!customFields.length && (
          <Callout
            title={i18n.t('employees.bulk_edit.custom_fields_callout.title')}
            description={i18n.t('employees.bulk_edit.custom_fields_callout.description')}
            type="warning"
          />
        )}
        <DispatchLocalChangeOnEmployeesProvider dispatch={dispatchLocalChangeOnEmployees}>
          <LocalChangesByEmployeeProvider changes={localChangesByEmployee}>
            <Body
              employees={employeesToShow}
              legalEntities={legalEntities}
              tableSelection={tableSelection}
              onSelect={onSelect}
              onAllSelect={onAllSelect}
              options={TableOptions}
              employeeGroups={employeeGroups}
              customFields={customFields}
              customFieldsValues={customFieldsValues}
            />
          </LocalChangesByEmployeeProvider>
        </DispatchLocalChangeOnEmployeesProvider>
      </Box>
    </ResponsivePage.Body>
  )
}

export default observer(ListEmployees)
